/**
 * Spec 004 US1 (T008) — Per-row status badge for /expenses.
 *
 * Renders the primary label as a pill + up to N secondary states as
 * smaller chips. Pure presentational; consumes
 * `ExpenseRowStatusDescriptor` from the shared derivation helper.
 *
 * Palette per research.md R8 (Tailwind dark-friendly tokens at 10-15%
 * opacity, preserves Spatial Glass transparency). Default state
 * intentionally renders nothing (the row's native background is the
 * signal).
 *
 * Secondary badges are clipped to MAX_SECONDARY visible chips with a
 * "+N" overflow indicator per spec.md § Edge Cases ("max 2 badges;
 * rest collapse into a '+N more' tooltip").
 */
import type {
  ExpenseRowStatus,
  ExpenseRowStatusDescriptor,
} from '@armard/shared';
import { STATUS_TONE_CLASSES, type StatusTone } from '../ui';

const MAX_SECONDARY = 2;

// Sprint 006 (A2): tones statt Eigenfarben — der frühere orange/amber-
// bzw. rose/red-Split kollabiert bewusst auf warning/danger (ratifizierte
// Role-Map). Die 8-State-Ableitung bleibt unverändert.
const PRIMARY_TONES: Record<ExpenseRowStatus, StatusTone | null> = {
  'quartal-closed': 'info',
  'ocr-failure': 'warning',
  betragsabweichung: 'danger',
  overdue: 'danger',
  'missing-date': 'warning',
  'missing-fields': 'warning',
  paid: 'success',
  default: null,
};

const PRIMARY_CLASSES: Record<ExpenseRowStatus, string> = Object.fromEntries(
  Object.entries(PRIMARY_TONES).map(([k, tone]) => [
    k,
    tone ? `border ${STATUS_TONE_CLASSES[tone]}` : '',
  ])
) as Record<ExpenseRowStatus, string>;

const SECONDARY_LABELS: Record<ExpenseRowStatus, string> = {
  'quartal-closed': 'Quartal abgeschlossen',
  'ocr-failure': 'OCR fehlgeschlagen',
  betragsabweichung: 'Betragsabweichung',
  overdue: 'Überfällig',
  'missing-date': 'Datum fehlt',
  'missing-fields': 'Felder fehlen',
  paid: 'Bezahlt',
  default: '',
};

interface Props {
  descriptor: ExpenseRowStatusDescriptor;
  className?: string;
}

export function ExpenseRowStatusBadge({ descriptor, className = '' }: Props) {
  const { primary, secondary, label, testid } = descriptor;

  if (primary === 'default') {
    return (
      <span
        data-testid={testid}
        className={`inline-flex items-center text-xs text-white/40 ${className}`}
        aria-label="Kein besonderer Status"
      >
        —
      </span>
    );
  }

  const visibleSecondary = secondary.slice(0, MAX_SECONDARY);
  const overflowSecondary = secondary.slice(MAX_SECONDARY);

  return (
    <span
      className={`inline-flex items-center gap-1 flex-wrap ${className}`}
      data-testid="expense-row-status-cell"
    >
      <span
        data-testid={testid}
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${PRIMARY_CLASSES[primary]}`}
      >
        {label}
      </span>
      {visibleSecondary.map((s) => (
        <span
          key={s}
          data-testid={`expense-row-badge-secondary-${s}`}
          className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] ${PRIMARY_CLASSES[s]}`}
          title={SECONDARY_LABELS[s]}
        >
          {SECONDARY_LABELS[s]}
        </span>
      ))}
      {overflowSecondary.length > 0 && (
        <span
          data-testid="expense-row-badge-overflow"
          className="inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] bg-white/5 border border-white/10 text-white/60"
          title={overflowSecondary.map((s) => SECONDARY_LABELS[s]).join(', ')}
        >
          +{overflowSecondary.length}
        </span>
      )}
    </span>
  );
}

/**
 * Tailwind row-tint class for the surrounding `<tr>` / row container.
 * Mirrors the primary palette but at lower opacity so it doesn't
 * compete with the inline badge.
 */
const TONE_TINTS: Record<StatusTone, string> = {
  neutral: 'bg-white/[0.03]',
  info: 'bg-blue-500/5',
  success: 'bg-emerald-500/5',
  warning: 'bg-amber-500/5',
  danger: 'bg-red-500/5',
  muted: 'bg-white/[0.02]',
  special: 'bg-purple-500/5',
};

export const ROW_TINT_CLASSES: Record<ExpenseRowStatus, string> = Object.fromEntries(
  Object.entries(PRIMARY_TONES).map(([k, tone]) => [k, tone ? TONE_TINTS[tone] : ''])
) as Record<ExpenseRowStatus, string>;
